/**
 * `candidate.hired` Event-Contract — **v1, eingefroren 2026-06-18**.
 *
 * Publisher: hirebotiq (Recruiting Tool)
 * Subscriber: operations (this repo, `/api/webhooks/candidate-hired`)
 *
 * Versionierung über `eventVersion`. Bei Major-Bump min. 30 Tage parallele
 * Unterstützung beider Versionen.
 *
 * Idempotenz: Operations dedupliziert über `eventId` in `processed_events`.
 *
 * Discriminator `data.engagementModel` steuert das Receiver-Verhalten
 * (Arbeitgeber-Achse): nur `HIREBOTIQ_AUE` => HireBotIQ ist Verleiher =>
 * voller PII-Onboarding. Siehe `isAgencyEmployment`.
 *
 * Bewusst NICHT im Payload (DSGVO-Minimalprinzip):
 *  - CV-Volltext, Skill-Embedding
 *  - ValuesProfile, CareerAspiration
 *  - Match-Score, Nova-Scoring-Details
 *
 * Wenn Operations CV-Inhalte braucht, holt es das explizit via Recruiting-API.
 */
import { z } from 'zod';

export const OPERATIONS_EVENT_VERSION_V1 = '1.0' as const;

/** ISO 8601-Datestring (UTC). */
const isoDate = z.string().datetime();

/** Authentik `sub`-Claim. */
const authentikSub = z.string().min(1);

/**
 * Kommerzielles Engagement-Modell des Hires — spiegelt `ENGAGEMENT_MODELS`
 * in `@hirebotiq/core` (hirebotiq Recruiting Tool).
 *
 * Bewusst dupliziert, NICHT importiert: Operations bleibt build-entkoppelt von
 * hirebotiq (Microservice-Grenze — siehe `adr-recruiting-tool-event-contract`).
 * Drift wird durch `candidate-hired.contract.test.ts` auf dieser Seite und den
 * Spiegel-Contract-Test auf der hirebotiq-Seite abgesichert. Ein 5. Wert ist
 * ein koordinierter Change auf beiden Seiten (kein stiller Enum-Zusatz).
 */
export const ENGAGEMENT_MODELS = [
  'HIREBOTIQ_AUE',
  'DIRECT_HIRE',
  'FREELANCE_CONTRACT',
  'INTERIM_MANAGEMENT',
] as const;

export const engagementModelSchema = z.enum(ENGAGEMENT_MODELS);
export type EngagementModel = z.infer<typeof engagementModelSchema>;

/**
 * True, wenn HireBotIQ in diesem Modell der rechtliche Arbeitgeber (Verleiher)
 * ist und Operations den vollen Mitarbeiter-Onboarding-Flow (Steuer-ID, SV,
 * IBAN, Aufenthaltstitel) fahren muss. Single Source of Truth für das
 * Receiver-Gate — Consumer hardcoden NICHT den String.
 *
 * Alle anderen Modelle: Kunde (bzw. der Worker selbst) ist Arbeitgeber =>
 * kein PII-Onboarding, nur Placement-Record + Staff-Flag.
 */
export function isAgencyEmployment(model: EngagementModel): boolean {
  return model === 'HIREBOTIQ_AUE';
}

export const candidateHiredLocationDetailsSchema = z
  .object({
    street: z.string().optional(),
    houseNumber: z.string().optional(),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    countryCode: z.string().length(2).optional(),
  })
  .strict();

export const candidateHiredCandidateSchema = z
  .object({
    id: z.string().uuid(),
    openId: authentikSub,
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().email(),
    phone: z.string().optional(),
    city: z.string().optional(),
    locationDetails: candidateHiredLocationDetailsSchema.optional(),
  })
  .strict();

export const candidateHiredMatchSchema = z
  .object({
    id: z.string().uuid(),
    jobId: z.string().uuid(),
    hiredAt: isoDate,
  })
  .strict();

/**
 * Der Kunde: Einsatzbetrieb / Entleiher bei `HIREBOTIQ_AUE`, direkter
 * Arbeitgeber bei `DIRECT_HIRE`. Bewusst `client` statt `employer`, weil der
 * rechtliche Arbeitgeber je nach `engagementModel` wechselt.
 */
export const candidateHiredClientSchema = z
  .object({
    id: z.string().uuid(),
    companyName: z.string().min(1),
  })
  .strict();

export const candidateHiredEventSchema = z
  .object({
    eventType: z.literal('candidate.hired'),
    eventVersion: z.literal(OPERATIONS_EVENT_VERSION_V1),
    eventId: z.string().uuid(),
    timestamp: isoDate,
    source: z.literal('recruiting-tool'),
    correlationId: z.string().uuid(),
    tenantId: z.string().uuid(),
    data: z
      .object({
        candidate: candidateHiredCandidateSchema,
        match: candidateHiredMatchSchema,
        client: candidateHiredClientSchema,
        engagementModel: engagementModelSchema,
      })
      .strict(),
  })
  .strict();

export type CandidateHiredEvent = z.infer<typeof candidateHiredEventSchema>;
